import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useState } from 'react'

import DataTable from '@/components/datatable/DataTable'
import ActionButton from '@/components/misc/ActionButton'
import UploadFileButton from '@/components/misc/UploadFileButton'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { TableBody, TableCell, TableRow } from '@/components/ui/table'
import useDialog from '@/hooks/useDialog'
import useNotyf from '@/hooks/useNotyf'
import { faPaperclip } from '@/lib/fontAwesomePro/regular'
interface Props {
  modalTitle?: string
  defaultSelectedFiles?: File[]
  icon?: React.ReactNode
  onConfirm: (files: File[]) => void
  maxFiles?: number
  maxFileSizeMb?: number
  multiple?: boolean
  buttonVariant?: React.ComponentProps<typeof Button>['variant']
  buttonSize?: React.ComponentProps<typeof Button>['size']
  buttonClassName?: string
  buttonLabel?: string
  isProcessing?: boolean
  confirmButtonText?: string
  children?: React.ReactNode | ((props: RenderProps) => React.ReactNode)
  accept?: string
}

interface RenderProps {
  render: () => React.ReactNode
  openFilePicker: () => void
}

const FilePicker: React.FC<Props> = ({
  modalTitle = 'Select files',
  defaultSelectedFiles = [],
  icon,
  onConfirm,
  maxFiles = Infinity,
  maxFileSizeMb,
  multiple = false,
  buttonVariant = 'outline-secondary',
  buttonLabel = 'Select files',
  buttonClassName,
  confirmButtonText = 'Confirm selection',
  buttonSize,
  isProcessing = false,
  children,
  accept,
}) => {
  const { isShowingDialog, showDialog, hideDialog } = useDialog()
  const notyf = useNotyf()
  const [files, setFiles] = useState<File[]>(defaultSelectedFiles)
  const canAddMore = files.length < maxFiles

  async function onAdd(file: File) {
    const isFileSizeError =
      maxFileSizeMb && file?.size && file.size > maxFileSizeMb * 1024 * 1024 // Convert MB to bytes

    if (isFileSizeError) {
      notyf.errorWithoutReporting({
        message: `File "${file.name}" exceeds max file size limit of ${maxFileSizeMb}MB`,
        duration: 6000,
      })
      return
    }

    setFiles((prevFiles) => [...prevFiles, file])
  }

  async function onConfirmClick() {
    if (files.length > maxFiles) {
      notyf.errorWithoutReporting({
        message: `You can add a maximum of ${maxFiles} files`,
        duration: 6000,
      })

      return
    }

    await onConfirm(files)
    setFiles([])
    hideDialog()
  }

  function onClose() {
    setFiles(defaultSelectedFiles)
    hideDialog()
  }

  function onRemove(index: number) {
    setFiles((prevFiles) => {
      return prevFiles.filter((_file, i) => i !== index)
    })
  }

  function render() {
    return (
      <>
        {defaultSelectedFiles.length === 0 && (
          <Button
            size={buttonSize}
            variant={buttonVariant}
            onClick={showDialog}
            className={buttonClassName}
          >
            {icon ? icon : <FontAwesomeIcon icon={faPaperclip} />}
            {buttonLabel}
          </Button>
        )}

        {isShowingDialog && (
          <Dialog open onHide={onClose}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{modalTitle}</DialogTitle>
              </DialogHeader>

              <DialogBody>
                {canAddMore && (
                  <UploadFileButton
                    onAdd={onAdd}
                    hasAddedFile={files.length > 0}
                    multiple={multiple}
                    accept={accept}
                  />
                )}

                {files.length > 0 && (
                  <DataTable tableContainerProps={{ className: 'mt-4' }}>
                    <colgroup>
                      <col />
                      <col style={{ width: '100px' }} />
                    </colgroup>

                    <TableBody>
                      {files.map((file, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <a href={URL.createObjectURL(file)} download>
                              {file.name}
                            </a>
                          </TableCell>

                          <TableCell>
                            <Button
                              variant="outline-destructive"
                              size="xs"
                              onClick={() => onRemove(index)}
                            >
                              Remove
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </DataTable>
                )}
              </DialogBody>

              <DialogFooter>
                <Button variant="outline-secondary" onClick={onClose}>
                  Cancel
                </Button>

                <ActionButton
                  variant="primary"
                  onClick={onConfirmClick}
                  isProcessing={isProcessing}
                  disabled={files.length === 0 || isProcessing}
                >
                  {confirmButtonText}
                </ActionButton>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </>
    )
  }

  return typeof children === 'function'
    ? children({ render, openFilePicker: () => showDialog() })
    : render()
}

export default FilePicker
